import React, { useState } from 'react';
import startCase from 'lodash/startCase';
import cn from 'classnames';
import PropTypes from 'prop-types';
import Checkbox from '../Checkbox';
import TextInput, { STYLE_TYPE } from '../Forms/Fields/TextInput';
import Select from '../Forms/Fields/Select';
import styles from './index.module.scss';
import text from 'static/text_display.json';
import DateTimePicker from '../DateTimePicker/DatePicker';
import {
  SearchIcon,
  BorderedCloseIcon,
  SubmitFilterIcon,
  CreateIcon,
  DownloadIcon,
  UploadIcon,
} from 'Icons';
import Switch from '../Forms/Fields/Switch/index.js';
import AsyncSelect from 'components/Forms/Fields/AsyncSelect';
import { Box, makeStyles } from '@material-ui/core';
import FilterTypeList from './filterTypeList';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';
import IconWithText from 'components/IconWithText/iconWithText';

const clearDependentFields = ({ linkedFilterFields, filterValues, field }) => {
  if (isEmpty(linkedFilterFields)) return filterValues;
  const cdf = { ...filterValues };
  if (
    !isEmpty(linkedFilterFields) &&
    Array.isArray(linkedFilterFields[field]) &&
    !isEmpty(linkedFilterFields[field])
  ) {
    linkedFilterFields[field].forEach(f => {
      if (cdf[f]) {
        cdf[f] = '';
      }
    });
  }
  return cdf;
};
const useStyles = makeStyles(() => ({
  root: {
    padding: '0.5rem 1rem 1rem',
  },
  bottomFilters: {
    display: 'flex',
    overflowX: 'auto',
    alignItems: 'flex-end',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '15px',
    padding: '16px',
  },
  switchSize: {
    width: '50px',
    display: 'block',
    alignItems: 'flex-end',
    wordWrap: 'break-word',
  },
  buttonSize: {
    width: '50px',
  },
  dateSelectorSize: {
    width: '150px',
  },
  selectBoxSize: {
    width: '150px',
  },
  inputLabelWidth: {
    width: '35px',
    height: '1.2rem',
    padding: `${0} !important`,
    display: 'flex',
  },
  textInputWidth: {
    width: '250px',
  },
  eventInputLabel: {
    // marginBottom: '-4px',
  },
  downloadUploadLabel: {
    // marginBottom: '-8px',
  },
}));

const filterOptions = ({
  item,
  key,
  values,
  handleChange,
  handleDate,
  handleCheckboxes,
  limitTags,
  classes,
}) => {
  switch (item.type) {
    case 'checkbox':
      return (
        <Checkbox
          onChange={handleCheckboxes}
          label={startCase(item.label)}
          name={item.field}
          key={key}
          value={values[item.field]}
        />
      );

    case 'toggle':
      return (
        <Switch
          className={cn(styles.switch, classes.switchSize)}
          onChange={handleCheckboxes}
          label={startCase(item.label)}
          name={item.field}
          key={key}
          value={values[item.field]}
          displayError={false}
          checked={values[item.field]}
          labelPosition="top"
          compact
        />
      );

    case 'select':
      return (
        <Select
          className={cn(classes.selectBoxSize)}
          name={item.field}
          label={startCase(item.label)}
          onChange={handleChange}
          fromFilters={true}
          options={item.options}
          value={values[item.field]}
          key={key}
          displayError={false}
          noOptionsText={item.noOptionsText}
          multiple={item.multiple}
          limitTags={limitTags}
          placeholder={item.placeholder}
        />
      );
    case 'async-select':
      return (
        <AsyncSelect
          className={cn(classes.selectBoxSize)}
          name={item.field}
          onChange={handleChange}
          value={!values[item.field] && item.multiple ? [] : values[item.field]}
          key={key}
          displayError={false}
          noOptionsText={item.noOptionsText}
          multiple={item.multiple}
          limitTags={limitTags}
          placeholder={item.placeholder}
          disabled={item.disabled}
          nullable={item.nullable}
          label={item.label ? item.label : startCase(item.field)}
          required={item.required}
          options={item.options}
          valueType={item.valueType}
          optionLabelKey={item.optionLabelKey}
          fetchOptions={item.fetchOptions}
          onChangeCallback={handleChange}
          fetchFilters={item.fetchFilters}
          dataTransform={item.dataTransform}
          fixedOptions={item.fixedOptions}
          useAsyncInFilters={item.useAsyncInFilters}
          resetWhen={item.resetWhen}
        />
      );
    case 'date-picker':
      return (
        <DateTimePicker
          key={key}
          className={cn(classes.dateSelectorSize)}
          onChange={value => handleDate(item.field, value)}
          label={startCase(item.label)}
          value={values[item.field]}
          minDate={item.minDate}
          showDateTime={false}
          compact
          displayError={false}
        />
      );

    default:
      return null;
  }
};
filterOptions.propTypes = {
  item: PropTypes.object,
  key: PropTypes.string,
  values: PropTypes.object,
  handleChange: PropTypes.func,
  handleDate: PropTypes.func,
  handleCheckboxes: PropTypes.func,
};

const Filters = ({
  id,
  downloadable,
  uploadable,
  editable,
  filters,
  reset,
  filterValues,
  setFilters,
  onIconClick,
  onUpdate,
  onDownloadClick,
  onUploadClick,
  resetFilterTextHover = text.general.reset_filters,
  // filterTextHover = text.general.filter,
  // createTextHover = text.general.create,
  downloadHoverText = text.general.download,
  uploadHoverText = text.general.upload,
  defaultFilterValues,
  limitTags,
  header,
  linkedFilterFields = {},
  showSearchFilter,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [toggleFilters, setToggleFilters] = useState({
    RegularFilters: true,
    AdvancedFilters: false,
  });
  const handleCheckboxes = e => {
    const field = e.target.name;
    const value = e.target.checked;

    setFilters({ ...filterValues, [field]: value }, false);
  };

  const handleChange = (e, fieldName, reason) => {
    if (reason === 'clear') {
      // get the field name you require for the filters and set the default value
      switch (fieldName) {
        case 'incidentStatus': {
          setFilters(
            {
              ...filterValues,
              [fieldName]: defaultFilterValues.incidentStatus,
            },
            false,
          );
          break;
        }
        case 'status': {
          setFilters(
            {
              ...filterValues,
              [fieldName]: defaultFilterValues?.status,
            },
            false,
          );
          break;
        }
        case 'taskType': {
          setFilters(
            {
              ...filterValues,
              [fieldName]: defaultFilterValues.taskType,
            },
            false,
          );
          break;
        }
        case 'severityRating': {
          setFilters(
            {
              ...filterValues,
              [fieldName]: [],
            },
            false,
          );
          break;
        }
        default: {
          break;
        }
      }
    }

    if (e?.type) {
      // non async components like select
      // Event has a type field
      const field = e.target.name;

      const val = e.target?.selectedValue
        ? e.target.selectedValue
        : e.target.value;

      const cdf = clearDependentFields({
        filterValues,
        linkedFilterFields,
        field,
      });

      setFilters({ ...cdf, [field]: val }, false);
    } else {
      // async components
      //NOTE: For multi select to work, the value should be either [] or null and
      // cannot be a string which is provided to materialUI's autoSelect value
      // Here e is an array

      const field = fieldName;
      const value = e;

      const cdf = clearDependentFields({
        filterValues,
        linkedFilterFields,
        field,
      });

      setFilters({ ...cdf, [field]: value }, false);
    }
  };

  const handleDate = (field, date) => {
    // false will not update the state to redux
    setFilters({ ...filterValues, [field]: date }, false);
  };

  const submit = () => {
    setFilters(
      {
        ...filterValues,
        ...(filterValues.taskType &&
          !filterValues.taskType.length && {
            taskType: defaultFilterValues?.taskType,
          }),
        ...(filterValues.status &&
          !filterValues.status.length && {
            status: defaultFilterValues?.status,
          }),
        ...(filterValues.incidentStatus &&
          !filterValues.incidentStatus.length && {
            incidentStatus: defaultFilterValues?.incidentStatus,
          }),
      }, // true helps to sync the filter state to the redux
      true,
    );
    onUpdate({
      ...filterValues,
      ...(filterValues.taskType &&
        !filterValues.taskType.length && {
          taskType: defaultFilterValues?.taskType,
        }),
      ...(filterValues.status &&
        !filterValues.status.length && {
          status: defaultFilterValues?.status,
        }),
      ...(filterValues.incidentStatus &&
        !filterValues.incidentStatus.length && {
          incidentStatus: defaultFilterValues?.incidentStatus,
        }),
    });
  };

  const onReset = () => {
    setFilters(
      { ...(showSearchFilter && { search: '' }), ...defaultFilterValues },
      true,
    );
    reset();
  };

  const handleFilterToggle = e => {
    setToggleFilters(prev => ({ ...prev, ...e }));
  };

  return (
    <Box component={'div'} className={classes.root}>
      <Box component={'div'} className={classes.bottomFilters} data-testid={id}>
        {filters && filterValues && (
          <>
            {showSearchFilter && (
              <TextInput
                id={`search_${id}`}
                styleType={STYLE_TYPE.iconBorder}
                field={'search'}
                value={filterValues && filterValues['search']}
                placeholder="Search"
                onChange={handleChange}
                className={cn(styles.textInput, classes.textInputWidth)}
                endAdornment={
                  <IconWithText
                    title="Search"
                    onClick={submit}
                    component={SearchIcon}
                  />
                }
                displayError={false}
              />
            )}
            {Object.keys(filters).map((filterKeys, index) => {
              const isToggledOn = toggleFilters[filterKeys];
              if (!isToggledOn) {
                return isToggledOn;
              }
              return (
                <React.Fragment key={`Filters_regular_advance${index}`}>
                  {filters[filterKeys].map((item, key) => {
                    return (
                      <Box component={'div'} key={key}>
                        {filterOptions({
                          item,
                          key,
                          values: filterValues,
                          limitTags,
                          handleCheckboxes,
                          handleChange,
                          handleDate,
                          classes: {
                            switchSize: classes.switchSize,
                            selectBoxSize: classes.selectBoxSize,
                            dateSelectorSize: classes.dateSelectorSize,
                          },
                        })}
                      </Box>
                    );
                  })}
                </React.Fragment>
              );
            })}
            {filters && Object.keys(filters).includes('AdvancedFilters') && (
              <FilterTypeList
                handleChange={handleFilterToggle}
                value={toggleFilters}
                availableFilters={Object.keys(filters)}
              />
            )}
            {filters && (
              <IconWithText
                text={'Apply Filters'}
                onClick={submit}
                component={SubmitFilterIcon}
              />
            )}
            {reset && filters && (
              <IconWithText
                text={resetFilterTextHover}
                onClick={onReset}
                component={BorderedCloseIcon}
                classes={classes.inputLabelWidth}
              />
            )}

            {downloadable && (
              <IconWithText
                text={downloadHoverText}
                onClick={onDownloadClick}
                component={DownloadIcon}
                classes={cn(
                  classes.inputLabelWidth,
                  classes.downloadUploadLabel,
                )}
              />
            )}
            {uploadable && (
              <IconWithText
                title={'Upload'}
                text={uploadHoverText}
                onClick={onUploadClick}
                component={UploadIcon}
                classes={cn(
                  classes.inputLabelWidth,
                  classes.downloadUploadLabel,
                )}
              />
            )}
          </>
        )}
        {editable && (
          <IconWithText
            text={`${t('Create')} ${t(header)}`}
            onClick={onIconClick}
            component={CreateIcon}
            classes={cn(classes.inputLabelWidth, classes.eventInputLabel)}
            testid={`toggleButton-${t('Create')}`}
          />
        )}
      </Box>
    </Box>
  );
};

Filters.propTypes = {
  id: PropTypes.string,
  editable: PropTypes.bool,
  icon: PropTypes.string,
  onIconClick: PropTypes.func,
  filters: PropTypes.object,
  reset: PropTypes.func,
  setFilters: PropTypes.func,
  filterValues: PropTypes.object,
  onUpdate: PropTypes.func,
  downloadable: PropTypes.bool,
  onDownloadClick: PropTypes.func,
  uploadable: PropTypes.bool,
  showSearchFilter: PropTypes.bool,
  onUploadClick: PropTypes.func,
  resetFilterTextHover: PropTypes.string,
  filterTextHover: PropTypes.string,
  createTextHover: PropTypes.string,
  header: PropTypes.string,
  downloadHoverText: PropTypes.string,
  uploadHoverText: PropTypes.string,
  defaultFilterValues: PropTypes.object,
  linkedFilterFields: PropTypes.object,
  limitTags: PropTypes.number,
};
Filters.defaultProps = {
  showSearchFilter: true,
};
export default Filters;
